import type { SessionContextType } from '../contexts/SessionContext';
import type { DeliveryBoardRow } from '../hooks/useDeliveryBoardFeed';

/**
 * Client-side mirror of the API receive opt-in gate (delivery.receivingEnabledAt).
 * The server enforces this too (403 when the org hasn't opted in, and silently
 * skips orders that predate the opt-in date) — this gating is UX only.
 */

/** Org-level gate: the org has opted in to marking parts received. */
export function canMarkPartsReceived(ctx: SessionContextType | undefined): boolean {
    if (!ctx || ctx.isAuthenticated !== true) return false;
    return ctx.deliveryReceiveEnabled === true;
}

/**
 * Per-row gate: org opted in, the order was created on/after the opt-in date,
 * and the row is something that can actually arrive (not `unfulfilled`,
 * not already received). The board itself is also nav-gated on opt-in and
 * server-filters to post-opt-in orders, so in-flight rows are uniformly receivable.
 */
export function isDeliveryRowReceivable(
    ctx: SessionContextType | undefined,
    row: DeliveryBoardRow,
): boolean {
    if (!canMarkPartsReceived(ctx)) return false;
    if (row.state === 'unfulfilled') return false;
    if (row.categories?.includes('unfulfilled')) return false;
    if (row.received) return false;
    if (!row.orderCreatedAt) return false;

    const enabledAt = ctx?.isAuthenticated === true ? ctx.deliveryReceiveEnabledAt : null;
    if (enabledAt && new Date(row.orderCreatedAt).getTime() < new Date(enabledAt).getTime()) {
        return false;
    }
    return true;
}
